/**
 * Client-side lazy signing for footwear-outputs storage refs.
 * Classification: shared/outputUrlRef.ts (same as server).
 */
import { apiFetch } from "@/lib/apiFetch";
import {
  classifyOutputRef,
  parseOutputStoragePath,
  withOutputCacheBust,
} from "@shared/outputUrlRef";

export {
  classifyOutputRef,
  parseOutputStoragePath,
  withOutputCacheBust,
} from "@shared/outputUrlRef";

const CACHE_TTL_MS = 50 * 60 * 1000; // refresh before server 3600s expiry
const MAX_BATCH = 64;

type CacheEntry = { url: string; expires: number };
const cache = new Map<string, CacheEntry>();

let pendingPaths = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const waiters = new Map<
  string,
  Array<{
    resolve: (url: string | null) => void;
  }>
>();

/** True when the ref must be signed (footwear-outputs path / public / signed URL). */
export function needsOutputSigning(stored: string | null | undefined): boolean {
  return classifyOutputRef(stored).kind === "sign";
}

function getCached(path: string): string | null {
  const hit = cache.get(path);
  if (hit && hit.expires > Date.now()) return hit.url;
  if (hit) cache.delete(path);
  return null;
}

function setCached(path: string, url: string) {
  cache.set(path, { url, expires: Date.now() + CACHE_TTL_MS });
}

/**
 * Drop a cached signed URL so the next resolveSignedOutputUrl call
 * mints a fresh one (used by Review Console resign-on-error).
 */
export function invalidateSignedOutputCache(
  stored: string | null | undefined,
): void {
  const classified = classifyOutputRef(stored);
  if (classified.kind === "sign") {
    cache.delete(classified.path);
  }
}

async function flushPending() {
  flushTimer = null;
  const batch = [...pendingPaths].slice(0, MAX_BATCH);
  pendingPaths = new Set([...pendingPaths].slice(MAX_BATCH));

  if (batch.length === 0) return;

  const uncached = batch.filter((p) => !getCached(p));
  let urls: Record<string, string> = {};

  if (uncached.length > 0) {
    try {
      const res = await apiFetch("/api/outputs/signed-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paths: uncached }),
      });
      const data = await res.json();
      if (data?.ok && data.urls && typeof data.urls === "object") {
        urls = data.urls as Record<string, string>;
        for (const [path, url] of Object.entries(urls)) {
          setCached(path, url);
        }
      }
    } catch {
      // waiters get null
    }
  }

  for (const path of batch) {
    const url = getCached(path) ?? urls[path] ?? null;
    const list = waiters.get(path) ?? [];
    waiters.delete(path);
    for (const w of list) w.resolve(url);
  }

  if (pendingPaths.size > 0) {
    flushTimer = setTimeout(() => {
      void flushPending();
    }, 16);
  }
}

/**
 * Resolve one stored ref to a display URL (batched signing under the hood).
 * External fal.ai URLs pass through unchanged; empty → null.
 */
export function resolveSignedOutputUrl(
  stored: string | null | undefined,
): Promise<string | null> {
  const classified = classifyOutputRef(stored);
  if (classified.kind === "empty") return Promise.resolve(null);
  if (classified.kind === "passthrough") {
    return Promise.resolve(classified.url);
  }

  const path = classified.path;
  const cached = getCached(path);
  if (cached) return Promise.resolve(cached);

  return new Promise((resolve) => {
    const list = waiters.get(path) ?? [];
    list.push({ resolve });
    waiters.set(path, list);
    pendingPaths.add(path);
    if (!flushTimer) {
      flushTimer = setTimeout(() => {
        void flushPending();
      }, 16);
    }
  });
}

/**
 * Eagerly sign a set of storage refs (visible library cards + lookahead).
 * Returns map of path → signed URL (passthrough refs omitted).
 */
export async function signOutputPaths(
  storedRefs: Array<string | null | undefined>,
): Promise<Record<string, string>> {
  const paths = [
    ...new Set(
      storedRefs
        .map((r) => {
          const c = classifyOutputRef(r);
          return c.kind === "sign" ? c.path : null;
        })
        .filter((p): p is string => !!p),
    ),
  ];
  const out: Record<string, string> = {};
  const need: string[] = [];
  for (const p of paths) {
    const c = getCached(p);
    if (c) out[p] = c;
    else need.push(p);
  }
  if (need.length === 0) return out;

  for (let i = 0; i < need.length; i += MAX_BATCH) {
    const chunk = need.slice(i, i + MAX_BATCH);
    try {
      const res = await apiFetch("/api/outputs/signed-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paths: chunk }),
      });
      const data = await res.json();
      if (data?.ok && data.urls) {
        for (const [path, url] of Object.entries(
          data.urls as Record<string, string>,
        )) {
          setCached(path, url);
          out[path] = url;
        }
      }
    } catch {
      // leave missing
    }
  }
  return out;
}
